import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, ScrollView, StyleSheet, ActivityIndicator, TouchableOpacity, RefreshControl, useWindowDimensions, TextInput, Image } from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaBullhorn, FaPlus, FaTwitter, FaTelegram, FaTiktok, FaInstagram, FaFacebookF, FaHeart, FaComment, FaShare, FaEye, FaTrash, FaArrowLeft, FaLink, FaChartLine, FaPause, FaPlay, FaChevronDown, FaChevronUp, FaSearch, FaPen, FaExternalLinkAlt, FaCheck, FaLock, FaUsers, FaBuilding, FaSync } from 'react-icons/fa';
import ListsManager from '../services/ListsManager';
import OrgManager from '../services/OrgManager';
import EngagementChart from '../components/EngagementChart';
import PostPickerGrid from '../components/PostPickerGrid';
import { useTheme } from '../theme/ThemeContext';
import { useOrg } from '../hooks/useOrg';

let THEME = {};
let T = {};

const buildT = (theme) => {
    const isDark = theme.bg === '#050507' || (theme.bg && theme.bg.startsWith('#0'));
    return isDark ? {
        bg:       '#0f1117',
        card:     '#1a1d27',
        surface:  '#22252f',
        border:   '#2e3140',
        text:     '#f1f5f9',
        textSec:  '#94a3b8',
        textMuted:'#64748b',
        primary:  '#38bdf8',
        success:  '#4ade80',
        warning:  '#fbbf24',
        error:    '#f87171',
        purple:   '#a78bfa',
    } : {
        bg:       '#ffffff',
        card:     '#ffffff',
        surface:  '#f9fafb',
        border:   '#e5e7eb',
        text:     '#111827',
        textSec:  '#374151',
        textMuted:'#9ca3af',
        primary:  '#0288d1',
        success:  '#16a34a',
        warning:  '#d97706',
        error:    '#dc2626',
        purple:   '#7c3aed',
    };
};

const buildCampaignDetailTheme = (theme) => {
    const t = buildT(theme);
    return {
        primary: t.primary,
        success: t.success,
        warning: t.warning,
        error: t.error,
        purple: t.purple,
        bg: t.bg,
        surface: t.surface,
        card: t.card,
        cardBorder: t.border,
        cardHoverBorder: '#7dd3fc',
        textMain: t.text,
        textSecondary: t.textSec,
        textTertiary: t.textMuted,
        bgSurface: t.surface,
        bgElevated: t.surface,
        inputBg: t.surface,
        modalBg: t.card,
        overlay: 'rgba(0,0,0,0.3)',
    };
};

const PLATFORM_COLORS = {
    twitter: '#1da1f2',
    telegram: '#0088cc',
    tiktok: '#ff0050',
    instagram: '#e1306c',
    facebook_group: '#1877F2',
};

const PLATFORM_ICONS = {
    twitter: FaTwitter,
    telegram: FaTelegram,
    tiktok: FaTiktok,
    instagram: FaInstagram,
    facebook_group: FaFacebookF,
};

const PLATFORMS = ['twitter', 'instagram', 'tiktok', 'telegram', 'facebook_group'];

const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

const formatNum = (n) => {
    if (!n) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return String(n);
};

export const CampaignDetailPanel = ({ campaignId: propId, onBack, onDeleted } = {}) => {
    const { theme: globalTheme, isDark } = useTheme();
    Object.assign(THEME, buildCampaignDetailTheme(globalTheme));
    styles = getStyles();
    const { t, i18n } = useTranslation();
    const params = useParams();
    const id = propId || params.id;
    const { width } = useWindowDimensions();
    const isDesktop = width >= 1024;
    const navigate = useNavigate();
    const { activeOrg } = useOrg();

    const [campaign, setCampaign] = useState(null);
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    // Inline editing for campaign settings
    const [editingName, setEditingName] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [editName, setEditName] = useState('');
    const [editDesc, setEditDesc] = useState('');

    // Visibility + access management
    const [visibility, setVisibility] = useState('org');
    const [accessList, setAccessList] = useState([]);
    const [orgMembers, setOrgMembers] = useState([]);
    const [accessLoading, setAccessLoading] = useState(false);

    // Add account modal — supports multiple accounts at once
    const [showAddAccount, setShowAddAccount] = useState(false);
    const [accountEntries, setAccountEntries] = useState([{ platform: 'twitter', username: '' }]);
    const [addingAccount, setAddingAccount] = useState(false);

    // Add post by URL
    const [postUrl, setPostUrl] = useState('');
    const [addingPost, setAddingPost] = useState(false);

    // Post picker — supports sequential queue for multi-account adds
    const [pickerAccountId, setPickerAccountId] = useState(null);
    const [pickerPlatform, setPickerPlatform] = useState(null);
    const [pickerUsername, setPickerUsername] = useState(null);
    const [previewPosts, setPreviewPosts] = useState([]);
    const [selectedPostIds, setSelectedPostIds] = useState(new Set());
    const [autoMonitor, setAutoMonitor] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [pickerQueue, setPickerQueue] = useState([]);  // queue of {id, platform, username}

    const [timelineData, setTimelineData] = useState({});
    const [commentsOverview, setCommentsOverview] = useState({});

    // Right panel width (draggable divider between middle and right)
    const [rightW, setRightW] = useState(560);
    const rightDragRef = useRef(null);
    const rightDragStartRef = useRef(null);

    const startRightDrag = (e) => {
        e.preventDefault();
        rightDragRef.current = true;
        rightDragStartRef.current = { x: e.clientX, rightW };
        const onMove = (mv) => {
            const rawDx = mv.clientX - rightDragStartRef.current.x;
            const dx = i18n.language === 'he' ? -rawDx : rawDx;
            setRightW(Math.max(300, Math.min(900, rightDragStartRef.current.rightW - dx)));
        };
        const onUp = () => {
            rightDragRef.current = null;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const fetchData = useCallback(async () => {
        try {
            setError(null);
            const [detail, dash] = await Promise.all([
                ListsManager.fetchCampaignDetail(id, activeOrg?.id),
                ListsManager.fetchCampaignDashboard(id),
            ]);
            if (detail) {
                setCampaign(detail);
                setVisibility(detail.visibility || 'org');
                const tkPosts = (detail.posts || []).filter(p => p.platform === 'tiktok');
                if (tkPosts.length > 0) console.log('🎵 TikTok posts:', tkPosts.map(p => ({ id: p.id, video_url: p.video_url, media_urls: p.media_urls, content: p.content?.substring(0, 60) })));
            } else setError(t('app.campaigns.campaignNotFound'));
            if (dash) setDashboard(dash);
        } catch (e) {
            console.error('Campaign detail fetch error:', e);
            setError(t('app.campaigns.failedToLoad'));
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [id]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Auto-load timeline + comments for every post as soon as posts are available
    useEffect(() => {
        const posts = campaign?.posts || [];
        if (posts.length === 0) return;
        posts.forEach(post => {
            if (!timelineData[post.id]) {
                ListsManager.fetchPostTimeline(id, post.id).then(result => {
                    const snapshots = result?.snapshots || (Array.isArray(result) ? result : null);
                    if (snapshots?.length > 0) setTimelineData(prev => ({ ...prev, [post.id]: snapshots }));
                }).catch(() => {});
            }
            if (!commentsOverview[post.id] && (post.platform === 'tiktok' || post.platform === 'instagram')) {
                ListsManager.fetchPostCommentsOverview(id, post.id).then(data => {
                    if (data) setCommentsOverview(prev => ({ ...prev, [post.id]: data }));
                }).catch(() => {});
            }
        });
    }, [campaign?.posts]);

    const onRefresh = useCallback(() => {
        setRefreshing(true);
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (visibility !== 'specific' || !activeOrg?.id) return;
        setAccessLoading(true);
        Promise.all([
            orgMembers.length === 0 ? OrgManager.listMembers(activeOrg.id) : Promise.resolve(orgMembers),
            OrgManager.getCampaignAccess(id),
        ]).then(([members, grants]) => {
            if (members && orgMembers.length === 0) setOrgMembers(members);
            const grantedIds = (grants || []).map(g => g.listener_id);
            setAccessList(grantedIds);
        }).finally(() => setAccessLoading(false));
    }, [visibility, activeOrg?.id, id]);

    const toggleAccess = (listenerId) => {
        setAccessList(prev =>
            prev.includes(listenerId) ? prev.filter(lid => lid !== listenerId) : [...prev, listenerId]
        );
    };

    const handleVisibilityChange = async (newVis) => {
        const prev = visibility;
        setVisibility(newVis);
        try {
            await ListsManager.updateCampaign(id, { visibility: newVis }, activeOrg?.id);
            setCampaign(c => ({ ...c, visibility: newVis }));
        } catch (e) {
            setVisibility(prev);
            console.error('Visibility update error:', e);
        }
    };

    const handleSyncAccess = async () => {
        const current = await OrgManager.getCampaignAccess(id);
        const currentIds = (current || []).map(g => g.listener_id);
        const toGrant = accessList.filter(lid => !currentIds.includes(lid));
        const toRevoke = currentIds.filter(lid => !accessList.includes(lid));
        await Promise.all([
            ...toGrant.map(lid => OrgManager.grantCampaignAccess(id, lid)),
            ...toRevoke.map(lid => OrgManager.revokeCampaignAccess(id, lid)),
        ]);
    };

    const handleToggleStatus = async () => {
        if (!campaign) return;
        const newStatus = campaign.status === 'active' ? 'paused' : 'active';
        try {
            await ListsManager.updateCampaign(id, { status: newStatus }, activeOrg?.id);
            setCampaign(prev => ({ ...prev, status: newStatus }));
        } catch (e) {
            console.error('Toggle status error:', e);
        }
    };

    const handleSaveName = async () => {
        const trimmed = editName.trim();
        if (!trimmed || trimmed === campaign.name) { setEditingName(false); return; }
        try {
            await ListsManager.updateCampaign(id, { name: trimmed }, activeOrg?.id);
            setCampaign(prev => ({ ...prev, name: trimmed }));
        } catch (e) {
            console.error('Save name error:', e);
        }
        setEditingName(false);
    };

    const handleSaveDesc = async () => {
        const trimmed = editDesc.trim();
        try {
            await ListsManager.updateCampaign(id, { description: trimmed }, activeOrg?.id);
            setCampaign(prev => ({ ...prev, description: trimmed }));
        } catch (e) {
            console.error('Save description error:', e);
        }
        setEditingDesc(false);
    };

    const handleDelete = async () => {
        if (!window.confirm(`Delete campaign "${campaign?.name}"? This cannot be undone.`)) return;
        try {
            await ListsManager.deleteCampaign(id, activeOrg?.id);
            if (onDeleted) onDeleted(id);
            else navigate('/campaigns');
        } catch (e) {
            console.error('Delete error:', e);
        }
    };

    const handleAddAccounts = async () => {
        const valid = accountEntries.filter(e => e.username.trim());
        if (valid.length === 0) return;
        setAddingAccount(true);
        try {
            const result = await ListsManager.addCampaignAccounts(id, valid.map(e => ({
                platform: e.platform,
                username: e.username.trim(),
            })));
            setAccountEntries([{ platform: 'twitter', username: '' }]);
            setShowAddAccount(false);
            await fetchData();
            // Queue all added accounts for sequential picker
            if (result?.accounts?.length > 0) {
                const queue = result.accounts.map(a => ({ id: a.id, platform: a.platform, username: a.username }));
                const first = queue[0];
                setPickerQueue(queue.slice(1));
                openPicker(first.id, first.platform, first.username);
            }
        } catch (e) {
            console.error('Add accounts error:', e);
        } finally {
            setAddingAccount(false);
        }
    };

    const updateAccountEntry = (index, field, value) => {
        setAccountEntries(prev => prev.map((e, i) => i === index ? { ...e, [field]: value } : e));
    };

    const addAccountEntry = () => {
        setAccountEntries(prev => [...prev, { platform: 'twitter', username: '' }]);
    };

    const removeAccountEntry = (index) => {
        if (accountEntries.length <= 1) return;
        setAccountEntries(prev => prev.filter((_, i) => i !== index));
    };

    const handleRemoveAccount = async (accountId, username) => {
        if (!window.confirm(`Remove account @${username}?`)) return;
        try {
            await ListsManager.removeCampaignAccount(id, accountId);
            setCampaign(prev => ({
                ...prev,
                accounts: prev.accounts.filter(a => a.id !== accountId),
            }));
        } catch (e) {
            console.error('Remove account error:', e);
        }
    };

    // --- Post Picker ---
    const openPicker = async (accountId, platform, username) => {
        setPickerAccountId(accountId);
        setPickerPlatform(platform);
        setPickerUsername(username);
        setSelectedPostIds(new Set());
        setAutoMonitor(false);
        setPreviewPosts([]);
        setLoadingPreview(true);
        try {
            const result = await ListsManager.previewAccountPosts(id, accountId);
            setPreviewPosts(result?.posts || []);
        } catch (e) {
            console.error('Preview posts error:', e);
        } finally {
            setLoadingPreview(false);
        }
    };

    const closePicker = () => {
        setPickerAccountId(null);
        setPreviewPosts([]);
        setSelectedPostIds(new Set());
    };

    const togglePostSelection = (postId) => {
        setSelectedPostIds(prev => {
            const next = new Set(prev);
            if (next.has(postId)) next.delete(postId);
            else next.add(postId);
            return next;
        });
    };

    const handleSelectAll = () => {
        if (selectedPostIds.size === previewPosts.length) {
            setSelectedPostIds(new Set());
        } else {
            setSelectedPostIds(new Set(previewPosts.map(p => p.platform_post_id)));
        }
    };

    const handleConfirmSelection = async () => {
        if (selectedPostIds.size === 0 || !pickerAccountId) return;
        try {
            await ListsManager.selectAccountPosts(id, pickerAccountId, Array.from(selectedPostIds), autoMonitor);
            closePicker();
            fetchData();
            // Advance to next account in queue
            if (pickerQueue.length > 0) {
                const next = pickerQueue[0];
                setPickerQueue(prev => prev.slice(1));
                openPicker(next.id, next.platform, next.username);
            }
        } catch (e) {
            console.error('Select posts error:', e);
        }
    };

    const handleSkipPicker = () => {
        closePicker();
        if (pickerQueue.length > 0) {
            const next = pickerQueue[0];
            setPickerQueue(prev => prev.slice(1));
            openPicker(next.id, next.platform, next.username);
        }
    };

    const handleToggleAutoMonitor = async (accountId, currentValue) => {
        try {
            await ListsManager.updateCampaignAccount(id, accountId, { auto_monitor: !currentValue });
            setCampaign(prev => ({
                ...prev,
                accounts: prev.accounts.map(a => a.id === accountId ? { ...a, auto_monitor: !currentValue } : a),
            }));
        } catch (e) {
            console.error('Toggle auto-monitor error:', e);
        }
    };

    const handleAddPost = async () => {
        if (!postUrl.trim()) return;
        setAddingPost(true);
        try {
            await ListsManager.addCampaignPost(id, postUrl.trim());
            setPostUrl('');
            fetchData();
        } catch (e) {
            console.error('Add post error:', e);
        } finally {
            setAddingPost(false);
        }
    };


    const handleRemovePost = async (postId, e) => {
        e.stopPropagation();
        if (!window.confirm('Remove this post from tracking?')) return;
        const result = await ListsManager.removeCampaignPost(id, postId);
        if (result?.ok) {
            setCampaign(prev => ({
                ...prev,
                posts: prev.posts.filter(p => p.id !== postId),
            }));
            setCommentsOverview(prev => { const n = { ...prev }; delete n[postId]; return n; });
        }
    };

    if (loading) {
        return (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexDirection: 'column', gap: 12, background: THEME.bg }}>
                <ActivityIndicator size="large" color={THEME.primary} />
                <span style={{ color: THEME.textSecondary, fontSize: 13 }}>{t('app.campaigns.loadingCampaign')}</span>
            </div>
        );
    }

    if (error || !campaign) {
        return (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexDirection: 'column', gap: 12, background: THEME.bg }}>
                <span style={{ color: THEME.error, fontSize: 16, fontWeight: 600 }}>{error || t('app.campaigns.campaignNotFound')}</span>
                <TouchableOpacity style={styles.retryButton} onPress={fetchData}>
                    <Text style={styles.retryText}>{t('app.common.retry')}</Text>
                </TouchableOpacity>
            </div>
        );
    }

    const accounts = campaign.accounts || [];
    const posts = campaign.posts || [];

    const css = `
        * { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
        .mono-value { font-family: 'SF Mono', 'Menlo', monospace; }
        .cdp-divider { width: 5px; flex-shrink: 0; cursor: col-resize; background: transparent; position: relative; z-index: 10; transition: background 0.15s; }
        .cdp-divider:hover, .cdp-divider:active { background: ${THEME.primary}55; }
        .cdp-divider::after { content: ''; position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); width: 1px; height: 32px; background: ${THEME.cardBorder}; border-radius: 1px; }
        .stat-card-inner { transition: border-color 0.15s; }
        .stat-card-inner:hover { border-color: #7dd3fc !important; }
        .section-card-glow { transition: border-color 0.15s; }
        .section-card-glow:hover { border-color: #7dd3fc !important; }
        .post-card { transition: border-color 0.15s; }
        .post-card:hover { border-color: #7dd3fc !important; }
        .stat-pill { transition: background 0.15s; }
        .stat-pill:hover { background: ${THEME.bgSurface} !important; }
    `;

    // ── Middle panel: campaign info + accounts ─────────────────────────────
    const middlePanel = (
        <div style={{
            flex: 1, overflowY: 'auto', padding: '16px 16px 32px',
            display: 'flex', flexDirection: 'column', gap: 12,
        }}>
            {/* ── Campaign identity block ── */}
            <div style={{
                borderRadius: 12, border: `1px solid ${THEME.cardBorder}`,
                background: THEME.card, overflow: 'hidden',
            }}>
                <div style={{ padding: '16px 16px 14px' }}>
                    {/* Name row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <div style={{
                            width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                            background: `${THEME.primary}15`, border: `1px solid ${THEME.primary}30`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <FaBullhorn size={17} color={THEME.primary} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            {editingName ? (
                                <TextInput style={[styles.inlineEditInput, { fontSize: 16, fontWeight: '700' }]}
                                    value={editName} onChangeText={setEditName}
                                    onBlur={handleSaveName} onSubmitEditing={handleSaveName} autoFocus />
                            ) : (
                                <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}
                                    onPress={() => { setEditName(campaign.name); setEditingName(true); }}>
                                    <Text style={{ color: THEME.textMain, fontSize: 17, fontWeight: '700', flex: 1 }} numberOfLines={1}>{campaign.name}</Text>
                                    <FaPen size={9} color={THEME.textTertiary} />
                                </TouchableOpacity>
                            )}
                        </div>
                    </div>

                    {/* Status + visibility + delete row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <TouchableOpacity style={[styles.statusBadge, campaign.status === 'active' ? styles.statusActive : styles.statusPaused]} onPress={handleToggleStatus}>
                            {campaign.status === 'active'
                                ? <><View style={[styles.statusDot, { backgroundColor: THEME.success }]} /><Text style={{ color: THEME.success, fontSize: 10, fontWeight: '700' }}>{t('app.campaigns.active')}</Text></>
                                : <><FaPause size={7} color={THEME.warning} /><Text style={{ color: THEME.warning, fontSize: 10, fontWeight: '700' }}>{t('app.campaigns.paused')}</Text></>}
                        </TouchableOpacity>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 20, background: THEME.bgSurface, border: `1px solid ${THEME.cardBorder}` }}>
                            {campaign.visibility === 'private' ? <FaLock size={7} color={THEME.textTertiary} /> : campaign.visibility === 'specific' ? <FaUsers size={7} color={THEME.textTertiary} /> : <FaBuilding size={7} color={THEME.textTertiary} />}
                            <span style={{ color: THEME.textTertiary, fontSize: 10, fontWeight: 600 }}>{campaign.visibility || 'org'}</span>
                        </div>
                        <div style={{ flex: 1 }} />
                        <TouchableOpacity style={{ padding: '4px 8px', borderRadius: 7, backgroundColor: THEME.error + '14', display: 'flex', alignItems: 'center', gap: 4, flexDirection: 'row' }} onPress={handleDelete}>
                            <FaTrash size={9} color={THEME.error} />
                            <span style={{ color: THEME.error, fontSize: 10, fontWeight: 600 }}>{t('app.campaigns.delete')}</span>
                        </TouchableOpacity>
                    </div>

                    {/* Description */}
                    <div style={{ marginTop: 10 }}>
                        {editingDesc ? (
                            <TextInput style={[styles.inlineEditInput, { fontSize: 12 }]}
                                value={editDesc} onChangeText={setEditDesc}
                                onBlur={handleSaveDesc} onSubmitEditing={handleSaveDesc}
                                placeholder={t('app.campaigns.addDescriptionPlaceholder')}
                                placeholderTextColor={THEME.textTertiary} autoFocus />
                        ) : campaign.description ? (
                            <TouchableOpacity onPress={() => { setEditDesc(campaign.description || ''); setEditingDesc(true); }}>
                                <div style={{ padding: '8px 10px', borderRadius: 9, background: THEME.bgSurface, border: `1px solid ${THEME.cardBorder}`, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ color: THEME.textTertiary, fontSize: 9, fontWeight: 600, marginBottom: 2 }}>{t('app.campaigns.descriptionLabel')}</div>
                                        <div style={{ color: THEME.textSecondary, fontSize: 12, lineHeight: '17px' }}>{campaign.description}</div>
                                    </div>
                                    <FaPen size={8} color={THEME.textTertiary} />
                                </div>
                            </TouchableOpacity>
                        ) : (
                            <TouchableOpacity onPress={() => { setEditDesc(''); setEditingDesc(true); }}>
                                <div style={{ padding: '6px 10px', borderRadius: 8, border: `1px dashed ${THEME.cardBorder}`, display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                                    <FaPen size={8} color={THEME.textTertiary} />
                                    <span style={{ color: THEME.textTertiary, fontSize: 11, fontStyle: 'italic' }}>{t('app.campaigns.addDescriptionPlaceholder')}</span>
                                </div>
                            </TouchableOpacity>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Stat row ── */}
            {dashboard && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
                    {[
                        { label: t('app.campaigns.posts'), value: formatNum(dashboard.stats?.total_posts), icon: <FaChartLine size={12} color={THEME.textTertiary} /> },
                        { label: t('app.campaigns.likes'), value: formatNum(dashboard.stats?.total_likes), icon: <FaHeart size={12} color={THEME.textTertiary} /> },
                        { label: t('app.campaigns.comments'), value: formatNum(dashboard.stats?.total_comments), icon: <FaComment size={12} color={THEME.textTertiary} /> },
                        { label: t('app.campaigns.shares'), value: formatNum(dashboard.stats?.total_shares), icon: <FaShare size={12} color={THEME.textTertiary} /> },
                        { label: t('app.campaigns.views'), value: formatNum(dashboard.stats?.total_views), icon: <FaEye size={12} color={THEME.textTertiary} /> },
                    ].map(s => (
                        <div key={s.label} style={{
                            background: THEME.card, borderRadius: 10,
                            border: `1px solid ${THEME.cardBorder}`,
                            padding: '10px 8px', display: 'flex', flexDirection: 'column',
                            alignItems: 'center', gap: 4,
                        }}>
                            {s.icon}
                            <span className="mono-value" style={{ fontSize: 16, fontWeight: 700, color: THEME.textMain }}>{s.value}</span>
                            <span style={{ color: THEME.textTertiary, fontSize: 9, fontWeight: 600 }}>{s.label}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Visibility Section */}
            <SectionCard title={t('app.campaigns.visibility')}>
                <View style={{ flexDirection: 'row', gap: 8, marginBottom: visibility === 'specific' ? 16 : 0 }}>
                    {[
                        { value: 'private', label: t('app.campaigns.visPrivate'), desc: t('app.campaigns.visPrivateDesc') },
                        { value: 'specific', label: t('app.campaigns.visSpecific'), desc: t('app.campaigns.visSpecificDesc') },
                        { value: 'org', label: t('app.campaigns.visOrg'), desc: t('app.campaigns.visOrgDesc') },
                    ].map(opt => (
                        <TouchableOpacity key={opt.value} onPress={() => handleVisibilityChange(opt.value)}
                            style={{ flex: 1, borderWidth: 1, borderRadius: 10, paddingVertical: 8, paddingHorizontal: 10, alignItems: 'center',
                                borderColor: visibility === opt.value ? '#0066cc60' : THEME.cardBorder,
                                backgroundColor: visibility === opt.value ? '#0066cc15' : THEME.bgSurface }}>
                            <Text style={{ color: visibility === opt.value ? '#0066cc' : THEME.textMain, fontWeight: '700', fontSize: 13 }}>{opt.label}</Text>
                            <Text style={{ color: THEME.textTertiary, fontSize: 11, marginTop: 2 }}>{opt.desc}</Text>
                        </TouchableOpacity>
                    ))}
                </View>
                {visibility === 'specific' && (
                    <View>
                        <Text style={{ color: THEME.textSecondary, fontSize: 13, fontWeight: '600', marginBottom: 8 }}>{t('app.campaigns.whoCanAccess')}</Text>
                        {accessLoading ? <ActivityIndicator size="small" color={THEME.primary} /> : orgMembers.length === 0 ? (
                            <Text style={{ color: THEME.textTertiary, fontSize: 13 }}>{t('app.campaigns.noOrgMembers')}</Text>
                        ) : (
                            <View style={{ gap: 4 }}>
                                {orgMembers.map(m => {
                                    const selected = accessList.includes(m.listener_id);
                                    return (
                                        <TouchableOpacity key={m.listener_id} onPress={() => {
                                            const isSelected = accessList.includes(m.listener_id);
                                            toggleAccess(m.listener_id);
                                            (isSelected ? OrgManager.revokeCampaignAccess(id, m.listener_id) : OrgManager.grantCampaignAccess(id, m.listener_id)).catch(e => console.error('Access toggle error:', e));
                                        }} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 8, paddingHorizontal: 10, borderRadius: 8,
                                            backgroundColor: selected ? '#0066cc10' : 'transparent', borderWidth: 1, borderColor: selected ? '#0066cc30' : THEME.cardBorder }}>
                                            <View style={{ width: 28, height: 28, borderRadius: 14, backgroundColor: '#0066cc25', justifyContent: 'center', alignItems: 'center' }}>
                                                <Text style={{ color: '#0066cc', fontWeight: '700', fontSize: 12 }}>{(m.username || m.email || '?')[0].toUpperCase()}</Text>
                                            </View>
                                            <Text style={{ color: THEME.textMain, fontSize: 13, flex: 1 }}>{m.username || m.email}</Text>
                                            <Text style={{ color: THEME.textTertiary, fontSize: 11 }}>{m.role}</Text>
                                            <View style={{ width: 20, height: 20, borderRadius: 10, backgroundColor: selected ? '#0066cc' : THEME.bgSurface,
                                                borderWidth: 1, borderColor: selected ? '#0066cc' : THEME.cardBorder, justifyContent: 'center', alignItems: 'center' }}>
                                                {selected && <FaCheck size={10} color="#fff" />}
                                            </View>
                                        </TouchableOpacity>
                                    );
                                })}
                            </View>
                        )}
                    </View>
                )}
            </SectionCard>

            {/* Tracked Accounts */}
            <SectionCard title={t('app.campaigns.trackedAccounts')} action={
                <TouchableOpacity style={styles.addBtn} onPress={() => setShowAddAccount(true)}>
                    <FaPlus size={12} color={THEME.primary} />
                    <Text style={styles.addBtnText}>{t('app.campaigns.addAccount')}</Text>
                </TouchableOpacity>
            }>
                {accounts.length === 0 ? (
                    <Text style={styles.emptyText}>{t('app.campaigns.noAccountsTracked')}</Text>
                ) : (
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
                        {accounts.map((acc) => {
                            const PlatformIcon = PLATFORM_ICONS[acc.platform] || FaTwitter;
                            const platColor = PLATFORM_COLORS[acc.platform] || '#888';
                            const isAutoMonitor = acc.auto_monitor;
                            const isPicking = pickerAccountId === acc.id;
                            const postCount = acc.post_count || 0;
                            return (
                                <View key={acc.id} style={{ width: '100%' }}>
                                    <View style={[styles.accountCard, isPicking && { borderColor: THEME.primary }]}>
                                        <View style={{ position: 'absolute', top: 12, right: 12, flexDirection: 'row', gap: 6, zIndex: 5 }}>
                                            <TouchableOpacity style={{ padding: 6, borderRadius: 8, backgroundColor: THEME.bgSurface }} onPress={() => handleRemoveAccount(acc.id, acc.username)}>
                                                <FaTrash size={10} color={THEME.textTertiary} />
                                            </TouchableOpacity>
                                        </View>
                                        <View style={{ flexDirection: 'row', gap: 14, alignItems: 'center', marginTop: 4 }}>
                                            <View style={{ position: 'relative' }}>
                                                {acc.profile_image_url ? (
                                                    <Image source={{ uri: acc.profile_image_url }} style={{ width: 44, height: 44, borderRadius: 22, borderWidth: 2, borderColor: platColor + '50' }} />
                                                ) : (
                                                    <View style={{ width: 44, height: 44, borderRadius: 22, background: '#e5e7eb', justifyContent: 'center', alignItems: 'center', borderWidth: 1, borderColor: THEME.cardBorder }}>
                                                        <PlatformIcon size={18} color={platColor} />
                                                    </View>
                                                )}
                                                <View style={{ position: 'absolute', bottom: -2, right: -2, width: 18, height: 18, borderRadius: 9, backgroundColor: THEME.card, justifyContent: 'center', alignItems: 'center', borderWidth: 2, borderColor: THEME.card }}>
                                                    <PlatformIcon size={8} color={platColor} />
                                                </View>
                                            </View>
                                            <View style={{ flex: 1 }}>
                                                <Text style={{ color: THEME.textMain, fontSize: 14, fontWeight: '700' }} numberOfLines={1}>{acc.display_name || acc.username}</Text>
                                                <Text style={{ color: THEME.textSecondary, fontSize: 12, marginTop: 1 }}>@{acc.username}</Text>
                                            </View>
                                        </View>
                                        <View style={{ flexDirection: 'row', gap: 8, marginTop: 12 }}>
                                            <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 20,
                                                backgroundColor: isAutoMonitor ? THEME.success + '12' : THEME.bgSurface, borderWidth: 1, borderColor: isAutoMonitor ? THEME.success + '25' : THEME.cardBorder }}
                                                onPress={() => handleToggleAutoMonitor(acc.id, isAutoMonitor)}>
                                                <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: isAutoMonitor ? THEME.success : THEME.textTertiary }} />
                                                <Text style={{ color: isAutoMonitor ? THEME.success : THEME.textTertiary, fontSize: 11, fontWeight: '600' }}>{isAutoMonitor ? 'Auto' : 'Manual'}</Text>
                                            </TouchableOpacity>
                                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 20,
                                                backgroundColor: postCount > 0 ? platColor + '10' : THEME.bgSurface, borderWidth: 1, borderColor: postCount > 0 ? platColor + '20' : THEME.cardBorder }}>
                                                <FaChartLine size={9} color={postCount > 0 ? platColor : THEME.textTertiary} />
                                                <Text style={{ color: postCount > 0 ? THEME.textSecondary : THEME.textTertiary, fontSize: 11, fontWeight: '600' }}>{postCount} post{postCount !== 1 ? 's' : ''}</Text>
                                            </View>
                                        </View>
                                        <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 12, paddingVertical: 10, borderRadius: 8,
                                            background: THEME.primary, borderWidth: 0 }}
                                            onPress={() => openPicker(acc.id, acc.platform, acc.username)}>
                                            <FaSearch size={11} color="#fff" />
                                            <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>{t('app.campaigns.browsePosts')}</Text>
                                        </TouchableOpacity>
                                    </View>
                                    {isPicking && (
                                        <View style={{ marginTop: 8 }}>
                                            {pickerQueue.length > 0 && (
                                                <View style={styles.pickerQueueBanner}>
                                                    <Text style={{ color: THEME.textSecondary, fontSize: 12 }}>{pickerQueue.length} more account{pickerQueue.length !== 1 ? 's' : ''} to set up</Text>
                                                </View>
                                            )}
                                            <PostPickerGrid posts={previewPosts} selectedIds={selectedPostIds} onToggle={togglePostSelection} onSelectAll={handleSelectAll}
                                                autoMonitor={autoMonitor} onAutoMonitorChange={setAutoMonitor} onConfirm={handleConfirmSelection} onClose={handleSkipPicker}
                                                loading={loadingPreview} platform={pickerPlatform} username={pickerUsername} queueRemaining={pickerQueue.length} />
                                        </View>
                                    )}
                                </View>
                            );
                        })}
                    </View>
                )}
            </SectionCard>

            {/* Track Post by URL */}
            <SectionCard title={t('app.campaigns.trackPostByUrl')}>
                <View style={{ flexDirection: 'row', gap: 10 }}>
                    <TextInput style={[styles.input, { flex: 1 }]} placeholder={t('app.campaigns.pastePostUrl')} placeholderTextColor={THEME.textTertiary} value={postUrl} onChangeText={setPostUrl} />
                    <TouchableOpacity style={[styles.trackBtn, !postUrl.trim() && { opacity: 0.5 }]} onPress={handleAddPost} disabled={!postUrl.trim() || addingPost}>
                        {addingPost ? <ActivityIndicator size="small" color="#fff" /> : (<><FaLink size={12} color="#fff" /><Text style={{ color: '#fff', fontWeight: '600', fontSize: 13 }}>{t('app.campaigns.track')}</Text></>)}
                    </TouchableOpacity>
                </View>
            </SectionCard>
        </div>
    );

    // ── Right panel: posts grid ────────────────────────────────────────────
    const rightPanel = (
        <div style={{
            width: isDesktop ? rightW : '100%', flexShrink: 0,
            borderLeft: isDesktop ? `1px solid ${THEME.cardBorder}` : 'none',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
            {/* Right panel header */}
            <div style={{
                padding: '14px 16px 12px', borderBottom: `1px solid ${THEME.cardBorder}`,
                flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8,
                background: THEME.card,
            }}>
                <FaChartLine size={12} color={THEME.primary} />
                <span style={{ color: THEME.textMain, fontSize: 13, fontWeight: 700, flex: 1 }}>
                    {t('app.campaigns.posts')}
                </span>
                {posts.length > 0 && (
                    <div style={{
                        background: `${THEME.primary}18`, border: `1px solid ${THEME.primary}30`,
                        padding: '2px 8px', borderRadius: 10,
                        fontSize: 11, fontWeight: 700, color: THEME.primary,
                    }}>{posts.length}</div>
                )}
                <div onClick={onRefresh} style={{ cursor: 'pointer', padding: '5px 10px', borderRadius: 8,
                    background: refreshing ? `${THEME.primary}20` : THEME.bgSurface,
                    border: `1px solid ${THEME.cardBorder}`, display: 'flex', alignItems: 'center', gap: 4 }}>
                    {refreshing ? <ActivityIndicator size="small" color={THEME.primary} /> : <FaSync size={10} color={THEME.textTertiary} />}
                    {!refreshing && <span style={{ color: THEME.textTertiary, fontSize: 10, fontWeight: 600 }}>{t('app.common.refresh')}</span>}
                </div>
            </div>

            {/* Scrollable post list — all analytics always visible */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px 24px' }}>
                {posts.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: 60, gap: 10 }}>
                        <FaChartLine size={24} color={THEME.textTertiary} />
                        <span style={{ color: THEME.textTertiary, fontSize: 13 }}>{t('app.campaigns.noPostsYet')}</span>
                        <span style={{ color: THEME.textTertiary, fontSize: 11 }}>{t('app.campaigns.addAccountsOrTrack')}</span>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {posts.map((post) => {
                            const platform = post.platform || 'twitter';
                            const PlatformIcon = PLATFORM_ICONS[platform] || FaTwitter;
                            const platColor = PLATFORM_COLORS[platform] || '#888';
                            const content = post.content || '';
                            const hasMedia = post.video_url || (post.media_urls && post.media_urls.length > 0);
                            const tiktokEmbedId = platform === 'tiktok'
                                ? ((post.post_url || post.video_url || '').match(/video\/(\d+)/) || [])[1] || null
                                : null;
                            const instagramShortcode = platform === 'instagram'
                                ? ((post.post_url || '').match(/instagram\.com\/(?:p|reel|tv)\/([A-Za-z0-9_-]+)/) || [])[1] || null
                                : null;
                            const co = commentsOverview[post.id];
                            const tl = timelineData[post.id];
                            const hasSentiment = platform === 'tiktok' || platform === 'instagram';
                            const posColor = isDark ? '#32D74B' : '#16a34a';
                            const negColor = isDark ? '#FF453A' : '#dc2626';
                            const neuColor = isDark ? '#636366' : '#94a3b8';
                            const posPct = co?.sentiment?.positive_pct || 0;
                            const negPct = co?.sentiment?.negative_pct || 0;
                            const neuPct = Math.max(0, 100 - posPct - negPct);
                            const isEmbedPost = tiktokEmbedId || instagramShortcode;
                            const statVals = [post.latest_likes || 0, post.latest_comments || 0, post.latest_views || 0, post.latest_shares || 0];
                            const maxStat = Math.max(...statVals);

                            return (
                                <div key={post.id} className="post-card" style={{
                                    borderRadius: 12, overflow: 'hidden',
                                    background: THEME.card,
                                    border: `1px solid ${THEME.cardBorder}`,
                                    position: 'relative',
                                }}>

                                    {/* ── ZONE A: Header ── */}
                                    <div style={{ paddingLeft: 20, paddingRight: 14, paddingTop: 14, paddingBottom: 12, display: 'flex', alignItems: 'center', gap: 10, borderBottom: `1px solid ${THEME.cardBorder}` }}>
                                        {/* Platform badge */}
                                        <div style={{
                                            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                                            background: platColor + '20',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <PlatformIcon size={13} color={platColor} />
                                        </div>
                                        {/* Author + date */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ color: THEME.textMain, fontSize: 13, fontWeight: 600, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                @{post.author_username}
                                            </div>
                                            <div style={{ color: THEME.textTertiary, fontSize: 11, marginTop: 1 }}>{formatDate(post.posted_at)}</div>
                                        </div>
                                        {/* Actions */}
                                        {post.post_url && (
                                            <div onClick={() => window.open(post.post_url, '_blank')} style={{ padding: '5px 8px', borderRadius: 7, background: THEME.bgSurface, border: `1px solid ${THEME.cardBorder}`, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                                <FaExternalLinkAlt size={9} color={THEME.textTertiary} />
                                            </div>
                                        )}
                                        <div onClick={(e) => handleRemovePost(post.id, e)} style={{ padding: '5px 8px', borderRadius: 7, background: THEME.error + '15', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                            <FaTrash size={9} color={THEME.error} />
                                        </div>
                                    </div>

                                    {/* ── ZONE B: Content ── */}
                                    {tiktokEmbedId ? (
                                        <div style={{ background: isDark ? '#0a0a0a' : '#f0f0f0', borderBottom: `1px solid ${THEME.cardBorder}` }}>
                                            <iframe src={`https://www.tiktok.com/embed/v2/${tiktokEmbedId}`}
                                                style={{ width: '100%', height: 420, border: 'none', display: 'block' }}
                                                allowFullScreen allow="encrypted-media"
                                                sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox" />
                                        </div>
                                    ) : instagramShortcode ? (
                                        <div style={{ background: isDark ? '#0a0a0a' : '#f0f0f0', borderBottom: `1px solid ${THEME.cardBorder}` }}>
                                            <iframe src={`https://www.instagram.com/p/${instagramShortcode}/embed/`}
                                                style={{ width: '100%', height: 480, border: 'none', display: 'block' }}
                                                allowFullScreen scrolling="no"
                                                sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox" />
                                        </div>
                                    ) : hasMedia ? (
                                        <div style={{ background: isDark ? '#000' : '#e8e8e8', maxHeight: 240, overflow: 'hidden', borderBottom: `1px solid ${THEME.cardBorder}` }}>
                                            {post.video_url
                                                ? <video src={post.video_url} controls playsInline style={{ width: '100%', objectFit: 'cover' }} />
                                                : <Image source={{ uri: post.media_urls[0] }} style={{ width: '100%', height: 220, resizeMode: 'cover' }} />}
                                        </div>
                                    ) : content ? (
                                        <div style={{ paddingLeft: 20, paddingRight: 16, paddingTop: 14, paddingBottom: 14, background: 'transparent', borderBottom: `1px solid ${THEME.cardBorder}` }}>
                                            <div style={{
                                                color: THEME.textMain, fontSize: 14, lineHeight: '22px',
                                                display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                            }}>{content}</div>
                                        </div>
                                    ) : null}

                                    {/* ── ZONE C: Performance ── */}
                                    <div style={{ paddingLeft: 20, paddingRight: 16, paddingTop: 14, paddingBottom: 14, borderBottom: `1px solid ${THEME.cardBorder}` }}>
                                        {/* Stat pills row */}
                                        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                                            {[
                                                { val: post.latest_likes, icon: <FaHeart size={11} color="#ff453a" />, label: t('app.campaigns.likes') },
                                                { val: post.latest_comments, icon: <FaComment size={11} color={platColor} />, label: t('app.campaigns.comments') },
                                                { val: post.latest_views, icon: <FaEye size={11} color={THEME.purple} />, label: t('app.campaigns.views') },
                                                { val: post.latest_shares, icon: <FaShare size={11} color={THEME.success} />, label: t('app.campaigns.shares') },
                                            ].map((s, i) => {
                                                const isTop = s.val > 0 && s.val === maxStat;
                                                return (
                                                    <div key={i} className="stat-pill" style={{
                                                        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                                                        padding: '8px 6px', borderRadius: 10,
                                                        background: THEME.bgSurface,
                                                        border: `1px solid ${isTop ? THEME.primary + '50' : THEME.cardBorder}`,
                                                        borderColor: isTop ? THEME.primary : THEME.cardBorder,
                                                    }}>
                                                        {s.icon}
                                                        <span className="mono-value" style={{ color: THEME.textMain, fontSize: isEmbedPost ? 16 : 18, fontWeight: 700, lineHeight: 1 }}>
                                                            {s.val > 0 ? formatNum(s.val) : '—'}
                                                        </span>
                                                        <span style={{ color: THEME.textTertiary, fontSize: 9, fontWeight: 600 }}>{s.label}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {/* Engagement chart */}
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                            <span style={{ color: THEME.textTertiary, fontSize: 10, fontWeight: 500 }}>
                                                {t('app.campaigns.engagementOverTime')}
                                            </span>
                                        </div>
                                        {tl ? (
                                            <EngagementChart data={tl} height={isEmbedPost ? 56 : 72} />
                                        ) : (
                                            <div style={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <ActivityIndicator size="small" color={THEME.primary} />
                                            </div>
                                        )}
                                    </div>

                                    {/* ── ZONE D: Sentiment ── */}
                                    {hasSentiment && (
                                        <div style={{ background: THEME.bgSurface }}>
                                            {!co ? (
                                                <div style={{ padding: 16, display: 'flex', justifyContent: 'center' }}>
                                                    <ActivityIndicator size="small" color={THEME.primary} />
                                                </div>
                                            ) : co.total_comments > 0 ? (<>
                                                {/* Sentiment header */}
                                                <div style={{ paddingLeft: 20, paddingRight: 16, paddingTop: 14, paddingBottom: 12 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                                                        <span style={{ color: THEME.textTertiary, fontSize: 10, fontWeight: 500, flex: 1 }}>
                                                            {t('app.campaigns.commentSentiment')}
                                                        </span>
                                                        <span style={{ color: THEME.textTertiary, fontSize: 10, background: THEME.bgElevated || THEME.card, padding: '2px 8px', borderRadius: 20, border: `1px solid ${THEME.cardBorder}` }}>
                                                            {co.total_comments}
                                                        </span>
                                                    </div>
                                                    {/* Sentiment bar */}
                                                    <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', marginBottom: 8 }}>
                                                        {posPct > 0 && <div style={{ flex: posPct, background: posColor }} />}
                                                        {neuPct > 0 && <div style={{ flex: neuPct, background: THEME.textTertiary + '60' }} />}
                                                        {negPct > 0 && <div style={{ flex: negPct, background: negColor }} />}
                                                    </div>
                                                    {/* Sentiment labels */}
                                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                            <div style={{ width: 5, height: 5, borderRadius: 3, background: posColor }} />
                                                            <span style={{ color: posColor, fontSize: 11, fontWeight: 600 }}>{posPct.toFixed(0)}% {t('app.campaigns.sentPos')}</span>
                                                        </span>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                            <div style={{ width: 5, height: 5, borderRadius: 3, background: THEME.textTertiary }} />
                                                            <span style={{ color: THEME.textTertiary, fontSize: 11 }}>{neuPct.toFixed(0)}% {t('app.campaigns.sentNeu')}</span>
                                                        </span>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                            <div style={{ width: 5, height: 5, borderRadius: 3, background: negColor }} />
                                                            <span style={{ color: negColor, fontSize: 11, fontWeight: 600 }}>{negPct.toFixed(0)}% {t('app.campaigns.sentNeg')}</span>
                                                        </span>
                                                    </div>
                                                </div>
                                                {/* Comment buckets */}
                                                {[
                                                    { label: t('app.campaigns.mostPositive'), color: posColor, comments: co.most_positive_comments },
                                                    { label: t('app.campaigns.mostLiked'), color: '#ffd60a', comments: co.most_liked_comments },
                                                    { label: t('app.campaigns.mostNegative'), color: negColor, comments: co.most_negative_comments },
                                                ].filter(s => s.comments?.length > 0).map(section => (
                                                    <div key={section.label} style={{ borderTop: `1px solid ${THEME.cardBorder}` }}>
                                                        <div style={{ paddingLeft: 20, paddingRight: 16, paddingTop: 8, paddingBottom: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <div style={{ width: 5, height: 5, borderRadius: 3, background: section.color, flexShrink: 0 }} />
                                                            <span style={{ color: section.color, fontSize: 10, fontWeight: 700 }}>{section.label}</span>
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 20, paddingRight: 16, paddingTop: 6, paddingBottom: 12 }}>
                                                            {section.comments.map(c => {
                                                                const sc = c.sentiment_score > 0.1 ? posColor : c.sentiment_score < -0.1 ? negColor : THEME.textTertiary;
                                                                return (
                                                                    <div key={c.id} style={{
                                                                        background: THEME.card,
                                                                        borderRadius: 8, padding: '9px 11px',
                                                                        border: `1px solid ${THEME.cardBorder}`,
                                                                    }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                                                                            <span style={{ color: THEME.textSecondary, fontSize: 11, fontWeight: 600, flex: 1 }}>@{c.author_username || '?'}</span>
                                                                            {c.sentiment_score != null && (
                                                                                <span style={{ color: sc, fontSize: 10, fontWeight: 700, background: sc + '1a', padding: '2px 7px', borderRadius: 20 }}>
                                                                                    {c.sentiment_score > 0.1 ? '+' : ''}{(c.sentiment_score).toFixed(2)}
                                                                                </span>
                                                                            )}
                                                                            {c.likes_count > 0 && (
                                                                                <span style={{ color: THEME.textTertiary, fontSize: 10, display: 'flex', alignItems: 'center', gap: 3 }}>
                                                                                    <FaHeart size={8} color="#ff453a" /> {formatNum(c.likes_count)}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <span style={{ color: THEME.textSecondary, fontSize: 12, lineHeight: '17px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{c.content}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ))}
                                            </>) : (
                                                <div style={{ paddingLeft: 20, paddingRight: 16, paddingTop: 12, paddingBottom: 12 }}>
                                                    <span style={{ color: THEME.textTertiary, fontSize: 12 }}>{t('app.campaigns.noCommentData')}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: css }} />
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden', background: THEME.bg }}>
                {middlePanel}
                {isDesktop && <div className="cdp-divider" onMouseDown={startRightDrag} />}
                {rightPanel}
            </div>

            {/* Add Accounts Modal */}
            {showAddAccount && (
                <View style={styles.modalOverlay}>
                    <View style={styles.modal}>
                        <Text style={styles.modalTitle}>{t('app.campaigns.addAccounts')}</Text>
                        <Text style={{ color: THEME.textSecondary, fontSize: 13, marginBottom: 4 }}>Add one or more accounts from any platform.</Text>
                        <View style={{ gap: 12, maxHeight: 400, overflow: 'auto' }}>
                            {accountEntries.map((entry, idx) => (
                                <View key={idx} style={styles.accountEntryRow}>
                                    <View style={styles.platformPicker}>
                                        {PLATFORMS.map((p) => {
                                            const Icon = PLATFORM_ICONS[p];
                                            const selected = entry.platform === p;
                                            return (
                                                <TouchableOpacity key={p} style={[styles.platformChip, selected && { backgroundColor: PLATFORM_COLORS[p] + '25', borderColor: PLATFORM_COLORS[p] }]}
                                                    onPress={() => updateAccountEntry(idx, 'platform', p)}>
                                                    <Icon size={14} color={selected ? PLATFORM_COLORS[p] : THEME.textTertiary} />
                                                </TouchableOpacity>
                                            );
                                        })}
                                    </View>
                                    <View style={{ flexDirection: 'row', gap: 8, flex: 1 }}>
                                        <TextInput style={[styles.input, { flex: 1 }]} placeholder="username" placeholderTextColor={THEME.textTertiary} value={entry.username}
                                            onChangeText={(val) => updateAccountEntry(idx, 'username', val)} autoFocus={idx === 0} />
                                        {accountEntries.length > 1 && (
                                            <TouchableOpacity style={styles.entryRemoveBtn} onPress={() => removeAccountEntry(idx)}>
                                                <FaTrash size={12} color={THEME.error} />
                                            </TouchableOpacity>
                                        )}
                                    </View>
                                </View>
                            ))}
                        </View>
                        <TouchableOpacity style={styles.addAnotherBtn} onPress={addAccountEntry}>
                            <FaPlus size={12} color={THEME.primary} />
                            <Text style={{ color: THEME.primary, fontSize: 13, fontWeight: '600' }}>{t('app.campaigns.addAnotherAccount')}</Text>
                        </TouchableOpacity>
                        <View style={styles.modalActions}>
                            <TouchableOpacity style={styles.cancelBtn} onPress={() => { setShowAddAccount(false); setAccountEntries([{ platform: 'twitter', username: '' }]); }}>
                                <Text style={styles.cancelBtnText}>{t('app.campaigns.cancel')}</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={[styles.confirmBtn, !accountEntries.some(e => e.username.trim()) && { opacity: 0.5 }]}
                                onPress={handleAddAccounts} disabled={!accountEntries.some(e => e.username.trim()) || addingAccount}>
                                {addingAccount ? <ActivityIndicator size="small" color="#fff" /> : (
                                    <Text style={styles.confirmBtnText}>Add {accountEntries.filter(e => e.username.trim()).length || ''} Account{accountEntries.filter(e => e.username.trim()).length !== 1 ? 's' : ''}</Text>
                                )}
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            )}
        </>
    );
};


const SectionCard = ({ title, action, children }) => (
    <View style={styles.sectionCard}>
        <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>{title}</Text>
            {action}
        </View>
        {children}
    </View>
);

const getStyles = () => StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: THEME.bg,
    },
    contentContainer: {
        maxWidth: 1400,
        marginHorizontal: 'auto',
        width: '100%',
        padding: 32,
        paddingBottom: 60,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: THEME.bg,
    },
    loadingText: {
        color: THEME.textSecondary,
        marginTop: 12,
        fontSize: 15,
    },
    errorText: {
        color: THEME.error,
        fontSize: 16,
        marginBottom: 12,
    },
    retryButton: {
        paddingHorizontal: 20,
        paddingVertical: 10,
        borderRadius: 8,
        backgroundColor: THEME.primary + '20',
    },
    retryText: {
        color: THEME.primary,
        fontWeight: '600',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
    },
    backBtn: {
        padding: 10,
        borderRadius: 10,
        backgroundColor: THEME.bgSurface,
    },
    campaignName: {
        color: THEME.textMain,
        fontSize: 26,
        fontWeight: '700',
    },
    campaignDesc: {
        color: THEME.textSecondary,
        fontSize: 14,
        marginTop: 2,
    },
    actionBtn: {
        padding: 10,
        borderRadius: 10,
        backgroundColor: THEME.bgSurface,
    },
    inlineEditInput: {
        color: THEME.textMain,
        backgroundColor: THEME.inputBg,
        borderWidth: 1,
        borderColor: THEME.primary + '60',
        borderRadius: 8,
        paddingHorizontal: 10,
        paddingVertical: 6,
        outlineStyle: 'none',
    },
    statusBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 10,
    },
    statusActive: {
        backgroundColor: THEME.success + '15',
    },
    statusPaused: {
        backgroundColor: THEME.warning + '15',
    },
    statusDot: {
        width: 7,
        height: 7,
        borderRadius: 4,
    },
    statsRow: {
        flexDirection: 'row',
        gap: 12,
        marginBottom: 20,
    },
    statCard: {
        flex: 1,
        minWidth: 100,
        backgroundColor: THEME.card,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        padding: 16,
        alignItems: 'center',
        gap: 8,
    },
    statIconCircle: {
        width: 32,
        height: 32,
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
    },
    statLabel: {
        color: THEME.textSecondary,
        fontSize: 12,
    },
    statValue: {
        fontSize: 22,
        fontWeight: '700',
    },
    sectionCard: {
        backgroundColor: THEME.card,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        padding: 24,
        marginBottom: 20,
    },
    sectionHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 14,
    },
    sectionTitle: {
        color: THEME.textMain,
        fontSize: 17,
        fontWeight: '700',
    },
    addBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 8,
        backgroundColor: THEME.primary + '20',
    },
    addBtnText: {
        color: THEME.primary,
        fontSize: 13,
        fontWeight: '600',
    },
    emptyText: {
        color: THEME.textTertiary,
        fontSize: 14,
        textAlign: 'center',
        paddingVertical: 20,
    },
    accountCard: {
        backgroundColor: THEME.card,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        padding: 18,
        overflow: 'hidden',
        position: 'relative',
    },
    platformBadge: {
        width: 30,
        height: 30,
        borderRadius: 8,
        justifyContent: 'center',
        alignItems: 'center',
    },
    autoMonitorOn: {
        backgroundColor: THEME.success + '15',
    },
    autoMonitorOff: {
        backgroundColor: THEME.bgSurface,
    },
    autoMonitorDot: {
        width: 6,
        height: 6,
        borderRadius: 3,
    },
    browsePostsBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 6,
        backgroundColor: THEME.primary + '15',
    },
    viewOriginalBtn: {
        padding: 6,
        borderRadius: 6,
        backgroundColor: THEME.bgSurface,
    },
    removePostBtn: {
        padding: 6,
        borderRadius: 6,
        backgroundColor: THEME.error + '18',
    },
    pickerQueueBanner: {
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 8,
        backgroundColor: THEME.primary + '10',
        marginBottom: 4,
        alignItems: 'center',
    },
    input: {
        backgroundColor: THEME.inputBg,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        borderRadius: 8,
        padding: 12,
        color: THEME.textMain,
        fontSize: 15,
    },
    trackBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 8,
        backgroundColor: THEME.primary,
    },
    postCard: {
        backgroundColor: THEME.bgSurface,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        padding: 14,
        gap: 10,
    },
    postHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    postAuthor: {
        color: THEME.textMain,
        fontSize: 13,
        fontWeight: '600',
        flex: 1,
    },
    postDate: {
        color: THEME.textTertiary,
        fontSize: 12,
    },
    postContent: {
        color: THEME.textSecondary,
        fontSize: 14,
        lineHeight: 20,
    },
    postEngagement: {
        flexDirection: 'row',
        gap: 16,
    },
    engagementItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    engagementVal: {
        color: THEME.textSecondary,
        fontSize: 12,
    },
    timelineSection: {
        marginTop: 8,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: THEME.cardBorder,
        gap: 10,
    },
    timelineTitle: {
        color: THEME.textMain,
        fontSize: 14,
        fontWeight: '600',
    },
    viewOriginal: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingTop: 8,
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: THEME.overlay,
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
    },
    modal: {
        backgroundColor: THEME.modalBg,
        borderRadius: 12,
        padding: 24,
        width: '90%',
        maxWidth: 480,
        gap: 12,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
    },
    modalTitle: {
        color: THEME.textMain,
        fontSize: 20,
        fontWeight: '700',
        marginBottom: 4,
    },
    fieldLabel: {
        color: THEME.textSecondary,
        fontSize: 13,
        fontWeight: '600',
    },
    platformPicker: {
        flexDirection: 'row',
        gap: 8,
        flexWrap: 'wrap',
    },
    platformOption: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        backgroundColor: THEME.bgSurface,
    },
    platformChip: {
        width: 34,
        height: 34,
        borderRadius: 8,
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        backgroundColor: THEME.bgSurface,
    },
    accountEntryRow: {
        gap: 8,
        padding: 12,
        borderRadius: 10,
        backgroundColor: THEME.bgSurface,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
    },
    entryRemoveBtn: {
        padding: 10,
        borderRadius: 8,
        justifyContent: 'center',
        alignItems: 'center',
    },
    addAnotherBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingVertical: 10,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: THEME.primary + '40',
        borderStyle: 'dashed',
    },
    platformOptionText: {
        color: THEME.textTertiary,
        fontSize: 13,
        fontWeight: '500',
    },
    modalActions: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 10,
        marginTop: 8,
    },
    cancelBtn: {
        paddingHorizontal: 20,
        paddingVertical: 10,
        borderRadius: 8,
    },
    cancelBtnText: {
        color: THEME.textSecondary,
        fontWeight: '600',
    },
    confirmBtn: {
        paddingHorizontal: 20,
        paddingVertical: 10,
        borderRadius: 8,
        backgroundColor: THEME.primary,
    },
    confirmBtnText: {
        color: '#fff',
        fontWeight: '600',
    },
});

let styles = getStyles();

const CampaignDetailScreen = () => <CampaignDetailPanel />;

export default CampaignDetailScreen;
